/**
 * One Production Line (production-lines): the coordinator that reads the
 * page model from `useProductionLineDetail` and composes the head, the five
 * tabs and the modals (brief, edit profile, pause, archive). Every section
 * lives in its own sibling file; this file only wires them together.
 */

import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EditProfileModal } from '../business-profile/BusinessProfilePage.js';
import {
  Button,
  ButtonLink,
  Callout,
  EmptyState,
  Modal,
  WarningIcon,
} from '../../merchant/ui/primitives.js';
import { NewProductionLineModal } from './NewProductionLineModal.js';
import { ProductionLineDetailHeader } from './ProductionLineDetailHeader.js';
import { ProductionLineOverviewTab } from './ProductionLineOverviewTab.js';
import { ProductionLineBriefTab } from './ProductionLineBriefTab.js';
import { ProductionLineAnalysisTab } from './ProductionLineAnalysisTab.js';
import { ProductionLineVideosTab } from './ProductionLineVideosTab.js';
import { ProductionLineActivityTab } from './ProductionLineActivityTab.js';
import { useProductionLineDetail } from './useProductionLineDetail.js';
import {
  currentMilestone,
  milestoneParam,
  MILESTONES,
  milestoneCopy,
  ProductionLineMilestones,
} from './ProductionLineMilestones.js';
import { RecommendationsStep } from './ProductionLineRecommendationsStep.js';
import { FirstIdeaEntry } from './FirstIdeaEntry.js';
import { useFirstIdeaCapability, usePendingIdeaStart } from './firstIdeaApi.js';
import { firstIdeaCopy } from './firstIdeaCopy.js';

/**
 * One Production Line, from the wireframe: the head with its status and
 * actions, five tabs, and — in Overview — the four-step journey the server
 * reports: Business Profile → Analysis → Recommendations → Creating your
 * video, then the completed video or the honest failure.
 *
 * Every step is read from the intake behind the line. Nothing advances on a
 * timer; the page only asks the server again while it says work is live.
 */
export function ProductionLineDetailPage(): ReactNode {
  const [searchParams] = useSearchParams();
  const firstIdeaEnabled = useFirstIdeaCapability();
  const {
    language,
    copy,
    navigate,
    toast,
    workspace,
    productionLineId,
    tab,
    titleId,
    online,
    detailFailed,
    moreOpen,
    setMoreOpen,
    modal,
    setModal,
    busy,
    actionError,
    selectedDirection,
    setSelectedDirection,
    askingAngle,
    setAskingAngle,
    setAnotherVersion,
    summary,
    realLine,
    journeyState,
    view,
    workspaceName,
    lineVideos,
    latestRun,
    journey,
    status,
    domain,
    prompt,
    title,
    updatedAt,
    startVideo,
    makeIt,
    askAngle,
    makeAnotherOnLine,
    lifecycle,
  } = useProductionLineDetail();
  const pendingIdeaStart = usePendingIdeaStart(view?.id);

  const current = currentMilestone(view, latestRun, journey);
  const requested = milestoneParam(searchParams.get('step'));
  const viewed =
    tab === 'analysis'
      ? 'research'
      : tab === 'videos'
        ? 'video'
        : tab === 'overview'
          ? (requested ?? current)
          : null;
  const otherStep =
    tab === 'overview' &&
    current !== null &&
    requested !== null &&
    requested !== current;
  const unavailableStep =
    otherStep && MILESTONES.indexOf(requested) > MILESTONES.indexOf(current);

  if (detailFailed && summary === null && view === null) {
    return (
      <section className="mj-ws-page" aria-labelledby={titleId}>
        <EmptyState
          body={copy.lines.lineMissing}
          action={
            <ButtonLink to="/production-lines" small>
              {copy.common.backToLines}
            </ButtonLink>
          }
        />
      </section>
    );
  }

  if (
    (firstIdeaEnabled === null || firstIdeaEnabled === 'unavailable') &&
    journey === 'profile' &&
    view?.profile?.reviewState === 'DRAFT' &&
    latestRun === null
  )
    return (
      <section className="mj-ws-page">
        <p role="status">
          {firstIdeaEnabled === null
            ? firstIdeaCopy[language].loading
            : firstIdeaCopy[language].entryUnavailable}
        </p>
        {firstIdeaEnabled === 'unavailable' && (
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            {firstIdeaCopy[language].reload}
          </Button>
        )}
      </section>
    );

  if (
    firstIdeaEnabled === true &&
    (journey === 'profile' || pendingIdeaStart?.body.firstStart === true) &&
    view?.profile &&
    (view.profile.reviewState === 'DRAFT' ||
      pendingIdeaStart?.body.firstStart === true) &&
    latestRun === null
  )
    return (
      <div className="mj-ws-page">
        <FirstIdeaEntry
          pendingStart={pendingIdeaStart}
          key={`${view.id}:${view.profile.artifactId}`}
          profile={view.profile.payload}
          sourceIntakeId={view.id}
          expectedProfileArtifactId={view.profile.artifactId}
          firstStart
          onStarted={(lineId, journeyId) => {
            void workspace.refresh();
            void navigate(`/production-lines/${lineId}?journey=${journeyId}`);
          }}
          onEdit={() => {
            setModal('edit-profile');
          }}
        />
        {modal === 'edit-profile' && (
          <EditProfileModal
            copy={copy}
            language={language}
            workspaceName={workspaceName}
            payload={view.profile.payload}
            busy={journeyState.working}
            onClose={() => {
              setModal(null);
            }}
            onSave={(next) => {
              void journeyState
                .correct(next, 'Merchant corrected the generated business profile.')
                .then((ok) => {
                  if (ok) {
                    setModal(null);
                    void workspace.refresh();
                  }
                });
            }}
          />
        )}
      </div>
    );
  return (
    <section
      className="mj-ws-page"
      aria-labelledby={titleId}
      data-screen-label="Production Line detail"
    >
      <ProductionLineDetailHeader
        copy={copy}
        language={language}
        titleId={titleId}
        productionLineId={productionLineId}
        title={title}
        status={status}
        domain={domain}
        prompt={prompt}
        updatedAt={updatedAt}
        summary={summary}
        realLine={realLine}
        busy={busy}
        moreOpen={moreOpen}
        setMoreOpen={setMoreOpen}
        setModal={setModal}
        lifecycle={lifecycle}
        navigate={navigate}
        toast={toast}
      />

      <ProductionLineMilestones
        current={current}
        viewed={viewed}
        lineId={productionLineId}
        language={language}
      />

      {actionError !== null ? (
        <Callout className="mj-ws-mt-8" role="alert">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">{actionError}</span>
        </Callout>
      ) : null}
      {journeyState.error !== null && tab === 'overview' ? (
        <Callout className="mj-ws-mt-8" role="alert">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">{journeyState.error}</span>
          <Button small onClick={journeyState.clearError}>
            {copy.common.close}
          </Button>
        </Callout>
      ) : null}

      {tab === 'overview' && !otherStep ? (
        <ProductionLineOverviewTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          journey={journey}
          view={view}
          journeyState={journeyState}
          workspaceName={workspaceName}
          domain={domain}
          prompt={prompt}
          title={title}
          summary={summary}
          realLine={realLine}
          latestRun={latestRun}
          online={online}
          busy={busy}
          selectedDirection={selectedDirection}
          setSelectedDirection={setSelectedDirection}
          askingAngle={askingAngle}
          setAskingAngle={setAskingAngle}
          setAnotherVersion={setAnotherVersion}
          setModal={setModal}
          startVideo={startVideo}
          makeIt={makeIt}
          askAngle={askAngle}
          makeAnotherOnLine={makeAnotherOnLine}
          toast={toast}
        />
      ) : null}

      {unavailableStep ? (
        <EmptyState body={milestoneCopy[language].unavailable} />
      ) : null}
      {otherStep && !unavailableStep && requested === 'business' ? (
        <ProductionLineBriefTab
          copy={copy}
          language={language}
          summary={summary}
          prompt={prompt}
          title={title}
          workspaceName={workspaceName}
          view={view}
          setModal={setModal}
        />
      ) : null}
      {otherStep && !unavailableStep && requested === 'research' ? (
        <ProductionLineAnalysisTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          view={view}
          journey={journey}
          journeyState={journeyState}
        />
      ) : null}
      {otherStep && !unavailableStep && requested === 'idea' && view !== null ? (
        <div className="mj-ws-mt-20">
          <RecommendationsStep
            copy={copy}
            language={language}
            workspaceName={workspaceName}
            view={view}
            selected={selectedDirection}
            busy={false}
            onSelect={setSelectedDirection}
            onCreate={startVideo}
            readOnly
          />
        </div>
      ) : null}
      {otherStep && !unavailableStep && requested === 'production' ? (
        <ProductionLineActivityTab
          copy={copy}
          language={language}
          summary={summary}
          view={view}
          lineVideos={lineVideos}
          realLine={realLine}
          domain={domain}
        />
      ) : null}

      {tab === 'brief' ? (
        <ProductionLineBriefTab
          copy={copy}
          language={language}
          summary={summary}
          prompt={prompt}
          title={title}
          workspaceName={workspaceName}
          view={view}
          setModal={setModal}
        />
      ) : null}

      {tab === 'analysis' ? (
        <ProductionLineAnalysisTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          view={view}
          journey={journey}
          journeyState={journeyState}
        />
      ) : null}

      {tab === 'videos' ? (
        <ProductionLineVideosTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          lineVideos={lineVideos}
        />
      ) : null}

      {tab === 'activity' ? (
        <ProductionLineActivityTab
          copy={copy}
          language={language}
          summary={summary}
          view={view}
          lineVideos={lineVideos}
          realLine={realLine}
          domain={domain}
        />
      ) : null}

      {modal === 'brief' ? (
        <NewProductionLineModal
          editing
          initialUrl={summary?.websiteUrl ?? ''}
          initialPrompt={prompt}
          onClose={() => {
            setModal(null);
          }}
          onCreated={(newIntakeId, lineId) => {
            setModal(null);
            void navigate(
              lineId === null
                ? `/business-profile/business?intake=${encodeURIComponent(newIntakeId)}`
                : `/production-lines/${encodeURIComponent(lineId)}`,
            );
          }}
        />
      ) : null}
      {modal === 'edit-profile' && view?.profile ? (
        <EditProfileModal
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          payload={view.profile.payload}
          busy={journeyState.working}
          onClose={() => {
            setModal(null);
          }}
          onSave={(next) => {
            void journeyState
              .correct(next, 'Merchant corrected the generated business profile.')
              .then((ok) => {
                if (ok) {
                  setModal(null);
                  toast(copy.profile.saved);
                  void workspace.refresh();
                }
              });
          }}
        />
      ) : null}
      {modal === 'pause' || modal === 'archive' ? (
        <Modal
          title={modal === 'pause' ? copy.line.pauseConfirm : copy.line.archiveConfirm}
          description={modal === 'pause' ? copy.line.pauseBody : copy.line.archiveBody}
          onClose={() => {
            setModal(null);
          }}
          onSubmit={() => {
            lifecycle(modal);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setModal(null);
                }}
              >
                {copy.common.cancel}
              </Button>
              <Button variant="primary" type="submit" disabled={busy !== null}>
                {modal === 'pause' ? copy.line.pause : copy.line.archive}
              </Button>
            </>
          }
        >
          <span className="mj-ws-vh">{title}</span>
        </Modal>
      ) : null}
    </section>
  );
}
